/**
 * Achievement Gallery Component
 * Display achievements with blockchain verification and sharing
 * Epic 75 - Individual Adult Self-Paced Learning
 */

import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Tile,
  Modal,
  Button,
  Tag,
  Loading,
  InlineNotification,
  Search,
  Pagination,
  OverflowMenu,
  OverflowMenuItem,
  Link,
  StructuredListWrapper,
  StructuredListHead,
  StructuredListBody,
  StructuredListRow,
  StructuredListCell,
} from '@carbon/react';
import { Trophy, CheckmarkFilled, Copy, View, Security, Calendar } from '@carbon/icons-react';

interface Achievement {
  achievement_id: string;
  achievement_type: 'completion' | 'mastery' | 'milestone' | 'streak' | 'excellence';
  achievement_name: string;
  description: string;
  criteria_met: {
    requirement: string;
    achieved_value: string;
    threshold: string;
  }[];
  badge_url?: string;
  earned_date: string;
  verification_hash: string;
  verification_url?: string;
  status: 'active' | 'verified' | 'pending' | 'expired';
  sharing_enabled: boolean;
}

interface AchievementGalleryProps {
  learnerId: string;
  onShareAchievement?: (achievementId: string) => void;
  onDownloadCertificate?: (achievementId: string) => void;
}

const AchievementGallery: React.FC<AchievementGalleryProps> = ({
  learnerId,
  onShareAchievement,
  onDownloadCertificate,
}) => {
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [filteredAchievements, setFilteredAchievements] = useState<Achievement[]>([]);
  const [selectedAchievement, setSelectedAchievement] = useState<Achievement | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [filterType, setFilterType] = useState<
    'all' | 'completion' | 'mastery' | 'milestone' | 'streak' | 'excellence'
  >('all');

  useEffect(() => {
    loadAchievements();
  }, [learnerId]);

  useEffect(() => {
    filterAchievements();
  }, [achievements, searchQuery, filterType]);

  const loadAchievements = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Mock data - in real implementation, call API
      const mockAchievements: Achievement[] = [
        {
          achievement_id: 'ach-001',
          achievement_type: 'completion',
          achievement_name: 'React Fundamentals Master',
          description: 'Successfully completed all React fundamentals courses with 90%+ scores',
          criteria_met: [
            { requirement: 'Course completion', achieved_value: '100%', threshold: '100%' },
            { requirement: 'Average score', achieved_value: '94%', threshold: '90%' },
            { requirement: 'Practice exercises', achieved_value: '15/15', threshold: '12/15' },
          ],
          badge_url: '/badges/react-fundamentals.svg',
          earned_date: '2024-11-15T10:30:00Z',
          verification_hash: '0x1a2b3c4d5e6f...',
          verification_url: 'https://blockchain.sigma08.com/verify/ach-001',
          status: 'verified',
          sharing_enabled: true,
        },
      ];

      setAchievements(mockAchievements);
    } catch (err) {
      setError('Failed to load achievements');
      console.error('Error loading achievements:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filterAchievements = () => {
    let filtered = achievements;

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(achievement => achievement.achievement_type === filterType);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(
        achievement =>
          achievement.achievement_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          achievement.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredAchievements(filtered);
    setCurrentPage(1);
  };

  const paginatedAchievements = filteredAchievements.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getAchievementIcon = (type: Achievement['achievement_type']) => {
    switch (type) {
      case 'completion':
        return <CheckmarkFilled />;
      case 'mastery':
        return <Trophy />;
      case 'milestone':
        return <Trophy />;
      case 'streak':
        return <Calendar />;
      case 'excellence':
        return <Security />;
      default:
        return <Trophy />;
    }
  };

  const getAchievementColor = (
    type: Achievement['achievement_type']
  ): 'blue' | 'cyan' | 'gray' | 'green' | 'magenta' | 'purple' | 'red' | 'teal' | 'warm-gray' => {
    switch (type) {
      case 'completion':
        return 'green';
      case 'mastery':
        return 'purple';
      case 'milestone':
        return 'blue';
      case 'streak':
        return 'warm-gray';
      case 'excellence':
        return 'red';
      default:
        return 'gray';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return <Loading withOverlay={false} />;
  }

  if (error) {
    return (
      <InlineNotification
        kind="error"
        title="Error loading achievements"
        subtitle={error}
        style={{ margin: 'var(--cds-spacing-05) 0' }}
      />
    );
  }

  return (
    <div style={{ padding: 'var(--cds-spacing-05) 0' }}>
      {/* Header and Filters */}
      <div style={{ marginBottom: 'var(--cds-spacing-07)' }}>
        <h2
          style={{
            fontSize: 'var(--cds-productive-heading-04-font-size)',
            fontWeight: 'var(--cds-productive-heading-04-font-weight)',
            color: 'var(--cds-text-primary)',
            marginBottom: 'var(--cds-spacing-03)',
          }}
        >
          Achievement Gallery
        </h2>
        <p style={{ color: 'var(--cds-text-secondary)' }}>Your learning achievements with blockchain verification</p>

        <div
          style={{
            display: 'flex',
            gap: 'var(--cds-spacing-05)',
            alignItems: 'center',
            flexWrap: 'wrap',
            marginTop: 'var(--cds-spacing-05)',
          }}
        >
          <Search
            labelText="Search achievements"
            placeholder="Search achievements..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            size="md"
            style={{ minWidth: '18rem' }}
          />

          <div style={{ display: 'flex', gap: 'var(--cds-spacing-03)' }}>
            {['all', 'completion', 'mastery', 'milestone', 'streak', 'excellence'].map(type => (
              <Button
                key={type}
                kind={filterType === type ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setFilterType(type as any)}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Achievement Grid */}
      <Grid>
        {paginatedAchievements.map(achievement => (
          <Column key={achievement.achievement_id} lg={4} md={4} sm={4}>
            <Tile
              style={{
                marginBottom: 'var(--cds-spacing-05)',
                backgroundColor: 'var(--cds-layer-02)',
                minHeight: '17.5rem',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Achievement Icon */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '5rem',
                  marginBottom: 'var(--cds-spacing-05)',
                }}
              >
                <div
                  style={{
                    height: '4rem',
                    width: '4rem',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'var(--cds-support-success)',
                    color: 'var(--cds-text-on-color)',
                  }}
                >
                  {getAchievementIcon(achievement.achievement_type)}
                </div>
              </div>

              {/* Achievement Info */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Tag
                  type={getAchievementColor(achievement.achievement_type)}
                  size="sm"
                  style={{ marginBottom: 'var(--cds-spacing-03)' }}
                >
                  {achievement.achievement_type}
                </Tag>
                <h3
                  style={{
                    fontSize: 'var(--cds-productive-heading-02-font-size)',
                    color: 'var(--cds-text-primary)',
                    marginBottom: 'var(--cds-spacing-03)',
                  }}
                >
                  {achievement.achievement_name}
                </h3>
                <p
                  style={{
                    color: 'var(--cds-text-secondary)',
                    fontSize: 'var(--cds-body-compact-01-font-size)',
                    marginBottom: 'var(--cds-spacing-05)',
                    flex: 1,
                  }}
                >
                  {achievement.description}
                </p>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 'var(--cds-spacing-03)',
                  }}
                >
                  <span
                    style={{
                      fontSize: 'var(--cds-body-compact-01-font-size)',
                      color: 'var(--cds-text-secondary)',
                    }}
                  >
                    {formatDate(achievement.earned_date)}
                  </span>
                  <Tag type="green" size="sm">
                    <Security size={12} style={{ marginRight: 'var(--cds-spacing-02)' }} />
                    Verified
                  </Tag>
                </div>

                <Button
                  kind="ghost"
                  size="sm"
                  renderIcon={View}
                  onClick={() => {
                    setSelectedAchievement(achievement);
                    setShowDetailModal(true);
                  }}
                  style={{ width: '100%' }}
                >
                  View Details
                </Button>
              </div>
            </Tile>
          </Column>
        ))}
      </Grid>

      {/* Achievement Detail Modal */}
      <Modal
        open={showDetailModal}
        onRequestClose={() => setShowDetailModal(false)}
        modalHeading={selectedAchievement?.achievement_name || 'Achievement Details'}
        primaryButtonText="Download Certificate"
        secondaryButtonText="Close"
        onRequestSubmit={() => {
          if (selectedAchievement && onDownloadCertificate) {
            onDownloadCertificate(selectedAchievement.achievement_id);
          }
          setShowDetailModal(false);
        }}
        size="md"
      >
        {selectedAchievement && (
          <div>
            <p style={{ marginBottom: 'var(--cds-spacing-05)' }}>{selectedAchievement.description}</p>
            {selectedAchievement.verification_url && (
              <div
                style={{
                  padding: 'var(--cds-spacing-05)',
                  backgroundColor: 'var(--cds-layer-01)',
                  borderRadius: '4px',
                  borderLeft: '4px solid var(--cds-support-success)',
                }}
              >
                <h5>Blockchain Verified</h5>
                <Link href={selectedAchievement.verification_url} target="_blank">
                  View on Blockchain
                </Link>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AchievementGallery;
